<template>
    <div class="chat-distribution-by-platform-chart" :class="{ fullscreen: fullScreen }">
        <div class="toolbar">
            <div class="left">
                <div class="buttons">
                    <IconButton
                        :classNames="tableMode ? '' : 'selected'"
                        :onClick="() => (tableMode = false)"
                        v-tippy="{ content: $t('report.chartView') }"
                    >
                        <md-icon>insights</md-icon>
                    </IconButton>
                    <IconButton
                        :classNames="tableMode ? 'selected' : ''"
                        :onClick="() => (tableMode = true)"
                        v-tippy="{ content: $t('report.tableView') }"
                    >
                        <md-icon>table_view</md-icon>
                    </IconButton>
                    <IconButton :onClick="exportToExcel" v-tippy="{ content: $t('report.download') }">
                        <md-icon>download</md-icon>
                    </IconButton>
                    <IconButton :onClick="switchToFullScreenMode" v-tippy="{ content: $t('report.fullScreen') }">
                        <md-icon v-if="!fullScreen">fullscreen</md-icon>
                        <md-icon v-else>fullscreen_exit</md-icon>
                    </IconButton>
                </div>
            </div>
        </div>
        <div class="chart-wrapper">
            <HalfPieChart
                :title="$t('report.chatDistByPlatform')"
                :sub-title="$t('report.chatDistByPDesc')"
                :series="getSeries()"
                v-if="!tableMode"
            />
            <TableView :cols="getCols()" :rows="getRows()" v-if="tableMode" />
        </div>
    </div>
</template>
<script>
import Vue from "vue";
import moment from "moment";
import * as echarts from "echarts/lib/echarts";
import _ from "lodash";
import IconButton from "../../Pages/Settings/components/IconButton.vue";
import StackedAreaChart from "../../Pages/Chatbot/pages/reports/charts/StackedAreaChart.vue";
import StatCard from "../../Pages/Chatbot/pages/reports/charts/StatCard.vue";
import TableView from "../../Pages/Chatbot/pages/reports/charts/TableView.vue";
import { expertToExcel } from "../../../utils/utils";
import log2 from "../../../utils/log2";
import DoubleBarChart from "../../Pages/Chatbot/pages/reports/charts/DoubleBarChart.vue";
import { mapState } from "vuex";
import fscreen from "fscreen";
import HalfPieChart from "../../Pages/Chatbot/pages/reports/charts/HalfPieChart.vue";

const log = log2("ChatDistributionsByPlatform");
export default Vue.extend({
    name: "ChatDistributionsByPlatform",
    components: { HalfPieChart, DoubleBarChart, TableView, IconButton, StatCard, StackedAreaChart },
    props: {
        filters: {
            type: Object,
            default: () => ({
                users: [],
                channels: [],
                startDate: moment().startOf("day").toISOString(),
                endDate: moment().endOf("day").toISOString(),
            }),
        },
        loading: {
            type: Boolean,
            default: true,
        },
        data: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            colors: ["#FFBF00"],
            legends: [this.$t("report.sessionCount")],
            stats: {
                MaxAgent: 0,
                MinAgent: 0,
                AvgAgent: 0,
            },
            tableMode: false,
            loadingInstance: null,
            fullScreen: false,
            cancels: [],
        };
    },
    computed: {
        ...mapState({}),
    },
    methods: {
        platformName(platformName) {
            return platformName.charAt(0).toUpperCase() + platformName.slice(1);
        },
        switchToFullScreenMode() {
            if (this.fullScreen) {
                this.closeFullScreenMode();
            } else {
                const elem = document.querySelector(".chat-distribution-by-platform-chart");
                fscreen.requestFullscreen(elem);
                this.fullScreen = true;
            }
        },
        closeFullScreenMode() {
            fscreen.exitFullscreen();
        },
        getCols() {
            return [
                {
                    title: "Platform",
                    dataIndex: "platform",
                    key: "platform",
                },
                {
                    title: this.$t("message.chatCount"),
                    dataIndex: "chatCount",
                    key: "chatCount",
                },
            ];
        },
        getRows() {
            const items = this.data || [];
            log("getRows.items", items);
            const rows = items.map((item) => {
                return {
                    platform: item.platform,
                    chatCount: item.totalChatCount,
                };
            });
            return rows;
        },
        getSeries() {
            let items = this.data || [];
            log("getSeries.items", items);

            const series = [
                {
                    type: "pie",
                    name: this.$t("message.chatCount"),
                    radius: ["40%", "70%"],
                    center: ["50%", "70%"],
                    // adjust the start and end angle
                    startAngle: 180,
                    endAngle: 360,
                    data: items.map((item) => {
                        return {
                            value: item.totalChatCount,
                            name: this.platformName(item.platform),
                        };
                    }),
                },
            ];

            return series;
        },
        exportToExcel() {
            let items = this.data || [];
            log("exportToExcel.items", items);

            const excelData = items.map((item) => {
                return {
                    Platform: item.platform,
                    "Total Chat Count": item.totalChatCount,
                };
            });
            expertToExcel(excelData, `chat_counts_by_platform.xlsx`, "Sheet 1");
        },
    },
    mounted() {
        this.loadingInstance = this.$veLoading({
            target: document.querySelector(".chat-distribution-by-platform-chart"),
            name: "wave",
        });
        if (this.loading) {
            this.loadingInstance.show();
        }
    },
    created() {
        const fullscreenCallback = () => {
            console.log("fullscreenCallback", fscreen.fullscreenElement);
            if (fscreen.fullscreenElement !== null) {
                this.fullScreen = true;
            } else {
                this.fullScreen = false;
            }
            this.$forceUpdate();
        };
        fscreen.addEventListener("fullscreenchange", fullscreenCallback);
        this.cancels.push(() => {
            fscreen.removeEventListener("fullscreenchange", fullscreenCallback);
        });
    },
    beforeDestroy() {
        this.cancels.forEach((cancel) => cancel());
    },
    watch: {
        loading(val) {
            if (val) {
                this.loadingInstance.show();
            } else {
                this.loadingInstance.close();
            }
        },
    },
});
</script>
<style lang="scss">
.chat-distribution-by-platform-chart {
    &.fullscreen {
        background-color: #fff;
        padding: 20px;
        overflow-y: scroll;

        .stacked-area-chart {
            height: calc(100vh - 200px);
        }
    }
}
</style>
<style scoped lang="scss">
.chat-distribution-by-platform-chart {
    display: flex;
    flex-direction: column;
    gap: 10px;

    .toolbar {
        display: flex;
        justify-content: space-between;

        .left {
            display: flex;

            .buttons {
                display: flex;
                border: 1px solid var(--color-border-default);

                .icon-button {
                    padding: 5px;
                    width: 40px;
                    height: 34px;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    border-right: 1px solid var(--color-border-default);
                    border-radius: unset !important;

                    &.selected {
                        background-color: var(--color-bg-info-low-hover);

                        i {
                            color: rgb(55, 162, 255);
                        }
                    }

                    &:hover {
                        background-color: var(--color-bg-neutral-pressed);
                    }
                }

                .icon-button:last-child {
                    border-right: none;
                }
            }
        }

        .right {
            display: flex;
            justify-content: flex-end;
        }
    }

    .chart-wrapper {
        min-height: 300px;
        height: auto;
    }

    .stats {
        display: flex;
        flex-direction: row;
        gap: 10px;
        flex-wrap: wrap;
    }
}
</style>
